import { useMemo, useState, type MouseEvent, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Download, Search } from "lucide-react";
import { Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { Button } from "@/design-system/components/ui";
import { ApiError } from "@/api/client";
import { formatDateInTz, useUserTimeZone } from "@/utils/datetime";
import {
  knowledgeBaseApi,
  type KbConfidentiality,
  type KbDocument,
  type KbCategory,
  type KbTag,
} from "@/api/knowledgeBase";

// Knowledge Base v1.0 PR 4 — Browse tab. The server (`GET /library`)
// already filters to the caller's accessible categories — this view never
// re-applies a client-side gate as the security boundary. We group rows by
// their category so the user sees one folder section per accessible area.

interface KbBrowseTabProps {
  categories: KbCategory[];
  tags: KbTag[];
}

export function KbBrowseTab({ categories, tags }: KbBrowseTabProps) {
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [tagFilter, setTagFilter] = useState<string>("");

  const browseQ = useQuery({
    queryKey: ["kb", "library", categoryFilter || null, tagFilter || null],
    queryFn: () =>
      knowledgeBaseApi.browseLibrary({
        categoryCode: categoryFilter || undefined,
        tagCode: tagFilter || undefined,
      }),
  });

  const filtered = useMemo(() => {
    const rows = browseQ.data ?? [];
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => r.title.toLowerCase().includes(needle));
  }, [browseQ.data, search]);

  const grouped = useMemo(() => groupByCategory(filtered, categories), [filtered, categories]);

  if (browseQ.isLoading) {
    return (
      <div className="text-caption text-gray-mid uppercase tracking-wide py-space-6 text-center">
        Loading library…
      </div>
    );
  }

  if (browseQ.isError) {
    return (
      <div className="text-small text-status-error py-space-4">
        {browseQ.error instanceof Error ? browseQ.error.message : "Failed to load library."}
      </div>
    );
  }

  const isEmpty = (browseQ.data ?? []).length === 0;

  return (
    <div className="space-y-space-4">
      <div>
        <h2 className="font-display font-bold text-h3 text-onyx normal-case">
          Browse the library
        </h2>
        <p className="text-body text-gray-mid mt-space-1 max-w-reading">
          The documents your position can access. Folders that aren't visible to
          your role don't appear here.
        </p>
      </div>

      <div className="flex flex-col md:flex-row md:items-end gap-space-3">
        <div className="flex-1 min-w-0">
          <label className="font-display text-label font-semibold uppercase tracking-label text-gray-mid block mb-space-1">
            Search
          </label>
          <div className="relative">
            <Search
              size={16}
              aria-hidden
              className="absolute left-space-2 top-1/2 -translate-y-1/2 text-gray-mid"
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title"
              className="pl-space-5"
              aria-label="Search library by title"
            />
          </div>
        </div>
        <div className="md:w-56">
          <label
            htmlFor="kb-browse-category"
            className="font-display text-label font-semibold uppercase tracking-label text-gray-mid block mb-space-1"
          >
            Category
          </label>
          <Select
            id="kb-browse-category"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">All accessible</option>
            {categories.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="md:w-56">
          <label
            htmlFor="kb-browse-tag"
            className="font-display text-label font-semibold uppercase tracking-label text-gray-mid block mb-space-1"
          >
            Tag
          </label>
          <Select
            id="kb-browse-tag"
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
          >
            <option value="">Any tag</option>
            {tags.map((t) => (
              <option key={t.code} value={t.code}>
                {t.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {isEmpty ? (
        <div className="py-space-6 text-center text-small text-gray-mid italic">
          No documents are available to your position yet.
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-space-6 text-center text-small text-gray-mid italic">
          No documents match your filter.
        </div>
      ) : (
        <div className="space-y-space-4">
          {grouped.map((group) => (
            <CategoryGroup key={group.categoryCode} group={group} tags={tags} />
          ))}
        </div>
      )}
    </div>
  );
}

interface CategoryGroupProps {
  group: { categoryCode: string; categoryName: string; rows: KbDocument[] };
  tags: KbTag[];
}

function CategoryGroup({ group, tags }: CategoryGroupProps) {
  return (
    <section aria-label={`Category ${group.categoryName}`}>
      <h3 className="font-display font-semibold text-small uppercase tracking-label text-gold-shadow mb-space-2">
        {group.categoryName}
      </h3>
      <ul className="divide-y divide-border-light border border-border-light rounded-md bg-white-pure">
        {group.rows.map((row) => (
          <li key={row.id}>
            <LibraryRow doc={row} tags={tags} />
          </li>
        ))}
      </ul>
    </section>
  );
}

const CONFIDENTIALITY_LABEL: Record<KbConfidentiality, string> = {
  CounterpartyFacing: "Counterparty-facing",
  InternalOnly: "Internal only",
};

function formatEffectiveDate(iso: string, tz: string): string {
  return formatDateInTz(iso, tz, { month: "short", day: "numeric", year: "numeric" }) || iso;
}

function LibraryRow({ doc, tags }: { doc: KbDocument; tags: KbTag[] }) {
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const tz = useUserTimeZone();

  const open = async (e: MouseEvent) => {
    e.stopPropagation();
    if (opening) return;
    setOpening(true);
    setError(null);
    try {
      const link = await knowledgeBaseApi.getDownloadLink(doc.id);
      window.open(link.sasUrl, "_blank", "noopener");
    } catch (err) {
      setError(
        err instanceof ApiError ? err.errors?.[0] ?? err.message : "Could not open document.",
      );
    } finally {
      setOpening(false);
    }
  };

  const tagLabels = doc.tagCodes.map(
    (code) => tags.find((t) => t.code === code)?.label ?? code,
  );

  return (
    <div className="px-space-3 py-space-2">
      <div className="flex items-center justify-between gap-space-3">
        <button
          type="button"
          aria-expanded={expanded}
          aria-label={`Expand details for ${doc.title}`}
          onClick={() => setExpanded((v) => !v)}
          className="flex-1 min-w-0 flex items-start gap-space-2 text-left bg-transparent border-0 p-0 cursor-pointer hover:opacity-80"
        >
          <span className="mt-space-1 shrink-0 text-gray-mid" aria-hidden>
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </span>
          <span className="flex-1 min-w-0">
            <span className="block font-body text-small text-onyx font-semibold truncate">
              {doc.title}
            </span>
            {doc.description && !expanded && (
              <span className="block text-caption text-gray-mid truncate max-w-reading">
                {doc.description}
              </span>
            )}
          </span>
        </button>
        <Button
          type="button"
          variant="tertiary"
          size="sm"
          onClick={open}
          loading={opening}
          aria-label={`Download ${doc.title}`}
        >
          <Download size={14} aria-hidden /> Open
        </Button>
      </div>
      {expanded && (
        <div className="mt-space-2 pl-space-5 space-y-space-2">
          {doc.description && (
            <div className="text-caption text-gray-mid max-w-reading whitespace-pre-wrap">
              {doc.description}
            </div>
          )}
          {tagLabels.length > 0 && (
            <MetaRow label="Tags">
              <span className="flex flex-wrap gap-space-1">
                {tagLabels.map((label, i) => (
                  <span
                    key={`${label}-${i}`}
                    className="inline-block rounded bg-gray-light px-space-2 py-space-1 text-caption text-onyx"
                  >
                    {label}
                  </span>
                ))}
              </span>
            </MetaRow>
          )}
          {doc.documentVersion && <MetaRow label="Version">{doc.documentVersion}</MetaRow>}
          {doc.effectiveDate && (
            <MetaRow label="Effective date">{formatEffectiveDate(doc.effectiveDate, tz)}</MetaRow>
          )}
          {doc.confidentiality && (
            <MetaRow label="Confidentiality">
              {CONFIDENTIALITY_LABEL[doc.confidentiality]}
            </MetaRow>
          )}
        </div>
      )}
      {error && (
        <div className="text-caption text-status-error mt-space-1" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

function MetaRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-space-2 text-caption">
      <span className="font-display uppercase tracking-label text-gray-mid w-32 shrink-0">
        {label}
      </span>
      <span className="flex-1 min-w-0 text-onyx">{children}</span>
    </div>
  );
}

function groupByCategory(rows: KbDocument[], categories: KbCategory[]) {
  const order = new Map(categories.map((c, i) => [c.code, { name: c.name, order: i }]));
  const buckets = new Map<string, { categoryCode: string; categoryName: string; rows: KbDocument[] }>();
  for (const row of rows) {
    const meta = order.get(row.primaryCategoryCode);
    const name = meta?.name ?? row.primaryCategoryCode;
    if (!buckets.has(row.primaryCategoryCode)) {
      buckets.set(row.primaryCategoryCode, {
        categoryCode: row.primaryCategoryCode,
        categoryName: name,
        rows: [],
      });
    }
    buckets.get(row.primaryCategoryCode)!.rows.push(row);
  }
  return Array.from(buckets.values()).sort((a, b) => {
    const ai = order.get(a.categoryCode)?.order ?? 999;
    const bi = order.get(b.categoryCode)?.order ?? 999;
    return ai - bi;
  });
}
